import { type ReactNode } from "react";
import { useDialogFocus } from "../../components/index.ts";

/**
 * A right-side sliding detail panel (modelled on the AgentDNA dashboard's drawer): a backdrop plus a
 * panel that slides in from the right, used to show the full detail of a selected row without
 * leaving the list. It is an accessible modal dialog — `useDialogFocus` moves focus in, traps Tab,
 * closes on Escape and returns focus to the trigger (design 12.4) — and the backdrop click closes it
 * too. Rendered only when `open`, so there is no inert dialog in the tree when nothing is selected.
 */
export function DetailDrawer(props: {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Accessible dialog title id target; the panel is labelled by the header's heading. */
  readonly labelledBy: string;
  readonly children: ReactNode;
}): ReactNode {
  const panelRef = useDialogFocus<HTMLDivElement>(props.onClose);
  if (!props.open) return null;
  return (
    <div className="drawer-root">
      <div className="drawer-backdrop" onClick={props.onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        className="drawer-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={props.labelledBy}
      >
        {props.children}
      </div>
    </div>
  );
}

/** A labelled section inside the drawer body. */
export function DrawerSection(props: { readonly title: string; readonly children: ReactNode }): ReactNode {
  return (
    <section className="drawer-section">
      <h3 className="drawer-section__title">{props.title}</h3>
      {props.children}
    </section>
  );
}

/** A key/value grid for the Metadata section. Pass alternating label/value children via rows. */
export function KeyValue(props: {
  readonly rows: ReadonlyArray<{ readonly k: string; readonly v: ReactNode }>;
}): ReactNode {
  return (
    <dl className="drawer-kv">
      {props.rows.map((row, i) => (
        <div className="drawer-kv__row" key={`${row.k}-${i}`}>
          <dt className="drawer-kv__k">{row.k}</dt>
          <dd className="drawer-kv__v">{row.v}</dd>
        </div>
      ))}
    </dl>
  );
}
